import { useEffect, useState } from 'react';
import {
  configApiRef,
  FeatureFlagsSaveOptions,
  featureFlagsApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { LocalStorageFeatureFlags } from '@backstage/core-app-api';

/**
 * Custom DOM event dispatched whenever a feature flag is toggled in the
 * current tab. Backstage's default `FeatureFlagsApi` writes to localStorage
 * but doesn't notify listeners on same-tab changes (browsers only fire the
 * native `storage` event for cross-tab writes), so we wrap the default impl
 * to broadcast on every `save` — `useFeatureFlag` listens to both events to
 * stay in sync without forcing a page refresh.
 */
export const FEATURE_FLAGS_CHANGED_EVENT = 'backstage:feature-flags-changed';

export const PERMISSIONS_TAB_FLAG = 'ft-enable-permissions-tab';

// RN-09 da S2.1 (group-management) — gateia o destino do item "Groups" na
// sidebar (SidebarNav.tsx): plugin novo com o toggle ligado, catálogo
// genérico (comportamento atual) com o toggle desligado. Mesmo mecanismo do
// RN-04 da S1.1 no backend (config.getOptionalBoolean com o mesmo path).
export const GROUP_MANAGEMENT_FLAG = 'spike.group-management.enabled';

/**
 * The localStorage key Backstage's `LocalStorageFeatureFlags` writes to. We
 * filter the native `storage` event by this key so unrelated cross-tab
 * writes don't cause spurious re-renders.
 */
const FEATURE_FLAGS_STORAGE_KEY = 'featureFlags';

/**
 * Drop-in replacement for `LocalStorageFeatureFlags` that emits a
 * `backstage:feature-flags-changed` window event whenever flags are saved.
 * Registered as the `featureFlagsApiRef` implementation in `apis.ts` so the
 * Settings page (and any other consumer of `featureFlagsApi.save`) becomes
 * observable from the rest of the app.
 */
export class ReactiveLocalStorageFeatureFlags extends LocalStorageFeatureFlags {
  save(options: FeatureFlagsSaveOptions): void {
    super.save(options);
    window.dispatchEvent(new CustomEvent(FEATURE_FLAGS_CHANGED_EVENT));
  }
}

/**
 * Reads a rollout flag from app-config (app.featureFlags.<name>).
 * Controlled centrally via backstage-chart values — not by the user.
 */
export function useRolloutFlag(name: string): boolean {
  const configApi = useApi(configApiRef);
  return configApi.getOptionalBoolean(`app.featureFlags.${name}`) ?? false;
}

/**
 * React hook that mirrors `featureFlagsApi.isActive(name)` but re-renders
 * the host component whenever the flag is toggled — either in the same tab
 * (via the custom `backstage:feature-flags-changed` event emitted by
 * `ReactiveLocalStorageFeatureFlags`) or in another tab (via the native
 * `storage` event for the `featureFlags` key).
 */
export function useFeatureFlag(name: string): boolean {
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const [active, setActive] = useState(() => featureFlagsApi.isActive(name));

  useEffect(() => {
    const sync = () => setActive(featureFlagsApi.isActive(name));
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== FEATURE_FLAGS_STORAGE_KEY) return;
      sync();
    };
    window.addEventListener(FEATURE_FLAGS_CHANGED_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(FEATURE_FLAGS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [featureFlagsApi, name]);

  return active;
}
